import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "../../api/client";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { tapFeedback } from "../feedback";
import { signed, todayLine } from "../format";
import { ScoreNumber } from "../components/ScoreNumber";
import { TapPad } from "../components/TapPad";
import { UndoToast } from "../components/UndoToast";
import { EventRow } from "../components/EventRow";
import { KidAvatar } from "../components/KidAvatar";
import { canSeeTaps } from "../../shared/roles";
import { closeOutdated } from "../../shared/stars";
import { dayKey } from "../../shared/time";
import type { PointEvent } from "../../shared/types";

/** One kid: the score up top, the tap pad for grown-ups, and today's log underneath. */
export function Kid({
  kidId,
  onBack,
  onReview,
  onDiary,
}: {
  kidId: string;
  onBack: () => void;
  onReview: () => void;
  onDiary: () => void;
}) {
  const { snapshot, addEvent, deleteEvent, loadDayEvents } = useStore();
  const kid = snapshot?.kids.find((k) => k.id === kidId);
  const isAdult = snapshot?.me.role === "adult";
  // A sibling's screen shows the score but not the taps: the Worker refuses them anyway.
  const canSee = snapshot ? canSeeTaps(snapshot.me, kidId) : false;
  const [events, setEvents] = useState<PointEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [lastTap, setLastTap] = useState<PointEvent | null>(null);
  // Only the newest load may write the log, so a slow early reply cannot hide a later tap.
  const loads = useRef(0);

  const reload = useCallback(async () => {
    if (!canSee) return;
    const mine = ++loads.current;
    try {
      const { events: list } = await loadDayEvents(kidId, dayKey(Date.now()));
      if (mine === loads.current) setEvents(list);
    } catch (e) {
      if (mine === loads.current) setErr(errorText(e, "Couldn't load today's taps."));
    }
  }, [kidId, canSee, loadDayEvents]);

  // Re-read whenever the snapshot moves (another phone's taps, the 30-second refresh), which
  // also picks up the new day after midnight: the day key is worked out on every read.
  useEffect(() => {
    void reload();
  }, [reload, snapshot]);

  // The kid was removed (on this phone or another): nothing to show.
  useEffect(() => {
    if (snapshot && !kid) onBack();
  }, [snapshot, kid, onBack]);

  if (!kid) return null;

  async function tap(delta: number, reason: string | null) {
    setErr(null);
    try {
      const e = await addEvent(kidId, delta, reason);
      // Replies can land out of order; the toast stays on the newer tap.
      setLastTap((p) => (p && p.at > e.at ? p : e));
    } catch (e) {
      setErr(errorText(e, "That tap didn't save."));
    }
    await reload();
  }

  /** Undo from the toast and Delete in today's list are the same call: the tap goes. */
  async function remove(event: PointEvent, failed: string) {
    tapFeedback(25);
    setLastTap((p) => (p?.id === event.id ? null : p));
    setErr(null);
    try {
      await deleteEvent(event.id);
    } catch (e) {
      setErr(errorText(e, failed));
    }
    await reload();
  }

  // The server keeps a tap that today's close holds, so only those made after it offer Delete.
  const deletable = (e: PointEvent) => isAdult && (!kid.today.closed || e.at > (kid.today.closedAt ?? Infinity));

  return (
    <main className="screen" style={kidStyle(kid.colour)}>
      <button className="back" type="button" onClick={onBack}>
        <span aria-hidden="true">‹</span> Back
      </button>

      <section className="kid-hero">
        <KidAvatar kid={kid} size="xl" />
        <h1 className="kid-hero__name">{kid.name}</h1>
        <ScoreNumber value={kid.score} className="kid-hero__score" />
        <p className="kid-hero__today">{todayLine(kid.todayNet)}</p>
      </section>

      <div className="kid-actions">
        <button className="button review-open" type="button" onClick={onReview}>
          {kid.today.closed ? "See today's review" : "Review today"}
        </button>
        <button className="button button--ghost" type="button" onClick={onDiary}>
          Diary
        </button>
      </div>

      {closeOutdated(kid.today, kid.score, kid.goal) && (
        <p className="hint close-moved">
          Score moved since the close
          {isAdult && (
            <>
              {" · "}
              <button className="link" type="button" onClick={onReview}>
                Update today's close
              </button>
            </>
          )}
        </p>
      )}

      {isAdult && <TapPad score={kid.score} onTap={(delta, reason) => void tap(delta, reason)} />}
      {err && <p className="error">{err}</p>}

      <section className="section">
        <h2 className="section__title">Today</h2>
        {!canSee ? (
          <p className="hint">{`Only grown-ups and ${kid.name} can see ${kid.name}'s taps.`}</p>
        ) : events.length === 0 ? (
          <p className="hint">No taps yet today.</p>
        ) : (
          <>
            {isAdult && events.some(deletable) && <p className="hint">Press and hold a tap to delete it.</p>}
            <ul className="event-list">
              {[...events].reverse().map((e) => (
                <EventRow
                  key={e.id}
                  event={e}
                  onDelete={deletable(e) ? () => void remove(e, "Couldn't delete that.") : undefined}
                />
              ))}
            </ul>
          </>
        )}
      </section>

      {lastTap && deletable(lastTap) && (
        <UndoToast
          key={lastTap.id}
          text={[signed(lastTap.delta), lastTap.reason].filter(Boolean).join(" ")}
          onUndo={() => void remove(lastTap, "Couldn't undo that.")}
          onDone={() => setLastTap(null)}
        />
      )}
    </main>
  );
}
